import React, { useState } from 'react';

interface LoginScreenProps {
  onJoin: (username: string, password?: string, profilePicture?: string) => void;
  error: string | null;
  isConnecting: boolean;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onJoin, error, isConnecting }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [showPasswordField, setShowPasswordField] = useState(false);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setShowPasswordField(value === 'nepcoder');
  };

  const handleProfileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    
    onJoin(username.trim(), password || undefined, profilePicture || undefined);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-telegram-darker dark:to-telegram-dark transition-all duration-300">
      <div className="w-full max-w-md">
        <div className="glass rounded-3xl p-8 shadow-2xl animate-bounce-in">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-telegram-blue to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="bi bi-shield-lock text-white text-3xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">PrivcyChat</h1>
            <p className="text-gray-600 dark:text-gray-300">Secure, ephemeral group messaging</p>
          </div>

          {/* Profile Upload */}
          <div className="text-center mb-6">
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 bg-gray-200 dark:bg-gray-700 flex items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-200 overflow-hidden border-4 border-white dark:border-gray-600 shadow-lg"
              onClick={() => document.getElementById('profileUpload')?.click()}
            >
              {profilePicture ? (
                <img src={profilePicture} alt="Profile" className="w-full h-full object-cover rounded-full" />
              ) : (
                <i className="bi bi-person-plus text-gray-500 dark:text-gray-400 text-2xl"></i>
              )}
            </div>
            <input 
              type="file" 
              id="profileUpload" 
              className="hidden" 
              accept="image/*"
              onChange={handleProfileUpload}
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">Tap to add profile photo</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Username Input */}
            <div className="mb-4">
              <div className="relative">
                <i className="bi bi-person absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input 
                  type="text" 
                  value={username}
                  onChange={handleUsernameChange}
                  className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl focus:ring-2 focus:ring-telegram-blue focus:border-transparent outline-none transition-all duration-200 text-gray-800 dark:text-white placeholder-gray-400" 
                  placeholder="Enter your username" 
                  maxLength={32}
                  required
                />
              </div>
            </div>

            {/* Admin Password */}
            {showPasswordField && (
              <div className="mb-6">
                <div className="relative">
                  <i className="bi bi-key absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input 
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl focus:ring-2 focus:ring-telegram-blue focus:border-transparent outline-none transition-all duration-200 text-gray-800 dark:text-white placeholder-gray-400" 
                    placeholder="Enter admin password"
                    required
                  />
                </div>
              </div>
            )}

            {/* Join Button */}
            <button 
              type="submit"
              disabled={isConnecting || !username.trim()}
              className="w-full bg-gradient-to-r from-telegram-blue to-blue-600 text-white py-4 rounded-2xl font-semibold text-lg hover:from-blue-600 hover:to-blue-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="bi bi-arrow-right-circle me-2"></i>
              {isConnecting ? 'Connecting...' : 'Start Chatting'}
            </button>
          </form>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Connection Status */}
          {isConnecting && (
            <div className="mt-4 p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-xl">
              <div className="flex items-center">
                <i className="bi bi-arrow-clockwise animate-spin me-2"></i>
                <span>Connecting to chat server...</span>
              </div>
              <div className="text-sm mt-2 opacity-75">
                This may take a few seconds. If it takes too long, please refresh the page.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;